import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import ScrollDatePicker from "../calendar/ScrollDatePicker";
import {
  actionGetFormasTerminacion,
  actionGetTerminacion,
  actionSaveTerminacion,
  actionDeleteTerminacion,
  actionUploadTerminacionDoc,
  actionDeleteTerminacionDoc,
  actionSaveConstanciaTerminacion,
} from "../../redux/actions/terminacion/terminacion";
import { PATH } from "../../redux/utils";
import { triggerHappyWave } from "../../utils/antdInsetWave";

const MESES_ES = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];
const fmtFechaLarga = (v) => `${v.format("DD")} DE ${MESES_ES[v.month()]} DE ${v.format("YYYY")}`;
const fmtFechaLargaHora = (v) => `${v.format("DD")} DE ${MESES_ES[v.month()]} DE ${v.format("YYYY")} A LAS ${v.format("HH:mm")} HORAS.`;

const formasTerminacion = [
  "Convenio",
  "Desistimiento",
  "Sentencia / Laudo favorable",
  "Sentencia / Laudo desfavorable",
  "Caducidad",
  "Sobreseimiento",
  "Incompetencia",
  "Acumulación a otro expediente",
];

const etapas = [
  "1 - Presentación de demanda",
  "2 - Acuerdos / Prevención",
  "3 - Admisión",
  "4 - Emplazamiento",
  "5 - Contestación y Auto de Admisión",
  "6 - Reconvención",
  "7 - Réplica",
  "8 - Contrarréplica",
  "9 - Integración de la litis",
  "10 - Audiencia Conciliatoria",
  "11 - Audiencia Preliminar",
  "12 - Audiencia de Juicio",
  "13 - Sentencia / Laudo",
  "14 - Amparo Directo",
  "15 - Ejecución",
];

const C = {
  blue: "#0A539A",
  blueHover: "#0D63B5",
  blueLight: "#DCE9F5",
  blueBorder: "#C7DDF0",
  amber: "#B7791F",
  amberLight: "#FEF6E7",
  amberBorder: "#F0D9A8",
  bg: "#F8FAFC",
  white: "#FFFFFF",
  text: "#111827",
  muted: "#6B7280",
  border: "#E2E8F0",
  red: "#DC2626",
  redHover: "#B91C1C",
  green: "#16A34A",
  greenLight: "#16A34A26",
  greenBorder: "#16A34A66",
  orange: "#B7791F",
  orangeLight: "#FEF6E7",
  orangeBorder: "#F0D9A8",
};

const formatCurrency = (val) => {
  if (val === "" || val === null || val === undefined) return "";
  const num = parseFloat(String(val).replace(/,/g, ""));
  if (isNaN(num)) return "";
  return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const input = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: `1px solid ${C.border}`, fontSize: 14,
  outline: "none", boxSizing: "border-box", background: C.white,
};

const select = { ...input, appearance: "auto" };

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function CurrencyInput({ value, onChange, style, placeholder }) {
  const [display, setDisplay] = useState(() =>
    value != null && value !== "" ? formatCurrency(value) : ""
  );
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) {
      setDisplay(value != null && value !== "" ? formatCurrency(value) : "");
    }
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/,/g, "");
    if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
    setDisplay(e.target.value);
    onChange(raw);
  };

  const handleFocus = (e) => {
    focused.current = true;
    const raw = String(value || "").replace(/,/g, "");
    setDisplay(raw);
    setTimeout(() => e.target.select(), 0);
  };

  const handleBlur = () => {
    focused.current = false;
    setDisplay(value != null && value !== "" ? formatCurrency(value) : "");
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder || "$0.00"}
      style={style}
    />
  );
}

function InfoCard({ label, value }) {
  return (
    <div style={{ padding: 14, borderRadius: 10, background: C.bg, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{value || "—"}</div>
    </div>
  );
}

export default function TabTerminacion({ expedienteId, onDatosActualizados }) {
  const dispatch = useDispatch();
  const { item, documentos, formas, loading } = useSelector((s) => s.terminacion);

  const [view, setView] = useState("form"); // "form" | "viewer"
  const [forma, setForma] = useState("");
  const [fecha, setFecha] = useState("");
  const [etapa, setEtapa] = useState("");
  const [obs, setObs] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]); // { file, name, id (local) }
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showReabrir, setShowReabrir] = useState(false);
  const [motivoReabrir, setMotivoReabrir] = useState("");
  const [showEliminar, setShowEliminar] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef();

  // Extra campos según forma
  const [montoConvenio, setMontoConvenio] = useState("");
  const [tipoPago, setTipoPago] = useState("");
  const [fechaPagoConvenio, setFechaPagoConvenio] = useState("");
  const [parcialidades, setParcialidades] = useState([]);
  const [montoCondenado, setMontoCondenado] = useState("");

  // Constancia de cumplimiento
  const [constanciaItem, setConstanciaItem] = useState(null); // datos cargados del backend
  const [showConstanciaForm, setShowConstanciaForm] = useState(true);
  const [constanciaFile, setConstanciaFile] = useState(null);
  const [constanciaFecha, setConstanciaFecha] = useState("");
  const [constanciaNotas, setConstanciaNotas] = useState("");
  const [savingConstancia, setSavingConstancia] = useState(false);
  const constanciaInputRef = useRef();
  const convenioInputRef = useRef();
  const [uploadingConvenio, setUploadingConvenio] = useState(false);

  useEffect(() => {
    if (!expedienteId) return;
    dispatch(actionGetFormasTerminacion());
    dispatch(actionGetTerminacion(expedienteId)).then((res) => {
      const t = res?.terminacion?.item;
      if (t) {
        setForma(String(t.id_forma_terminacion));
        setFecha(t.fecha_terminacion || "");
        setEtapa(t.etapa || "");
        setObs(t.observaciones || "");
        setMontoConvenio(t.monto_total || "");
        setMontoCondenado(t.monto_total || "");
        setTipoPago(t.tipo_pago ? String(t.tipo_pago) : "");
        setFechaPagoConvenio(t.fecha_pago || "");
        if (t.parcialidades?.length > 0) {
          setParcialidades(t.parcialidades.map((p) => ({ ...p, localId: Math.random() })));
        }
        if (t.is_constancia) {
          setConstanciaItem({
            filename: t.filename,
            path: t.path,
            constancia_url: t.constancia_url,
            fecha_cumplimiento: t.fecha_cumplimiento,
            notas_cumplimiento: t.notas_cumplimiento,
          });
        }
        setView("viewer");
      }
    });
  }, [expedienteId]);

  const opcionesForma = formas.length > 0
    ? formas.map((f) => ({ value: String(f.id_forma_terminacion), label: f.nombre }))
    : formasTerminacion.map((nombre, i) => ({ value: String(i + 1), label: nombre }));

  const nombreForma = (id) => {
    const op = opcionesForma.find((x) => x.value === String(id));
    return op?.label || id;
  };

  const handleFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    const mapped = files.map((f) => ({ file: f, name: f.name, localId: Math.random() }));
    setPendingFiles((prev) => [...prev, ...mapped]);
    e.target.value = "";
  };

  const removePending = (localId) => {
    setPendingFiles((prev) => prev.filter((f) => f.localId !== localId));
  };

  const addParcialidad = () => {
    setParcialidades((prev) => [...prev, { localId: Math.random(), monto: "", fecha_pago: "" }]);
  };
  const removeParcialidad = (localId) => {
    setParcialidades((prev) => prev.filter((p) => p.localId !== localId));
  };
  const updateParcialidad = (localId, field, value) => {
    setParcialidades((prev) => prev.map((p) => p.localId === localId ? { ...p, [field]: value } : p));
  };

  const handleDeleteDoc = async (id_doc) => {
    try {
      await dispatch(actionDeleteTerminacionDoc(expedienteId, id_doc));
      await dispatch(actionGetTerminacion(expedienteId));
    } catch (_) {}
  };

  const handleDirectUploadConvenio = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    setUploadingConvenio(true);
    try {
      await dispatch(actionUploadTerminacionDoc(expedienteId, f));
      await dispatch(actionGetTerminacion(expedienteId));
    } catch (_) {}
    finally { setUploadingConvenio(false); }
  };

  const handleSaveConstancia = async () => {
    if (!constanciaFecha) return;
    setSavingConstancia(true);
    try {
      const result = await dispatch(actionSaveConstanciaTerminacion(expedienteId, {
        fecha_cumplimiento: constanciaFecha,
        notas_cumplimiento: constanciaNotas,
        file: constanciaFile,
      }));
      setConstanciaItem({
        filename: result?.filename || constanciaFile?.name || null,
        constancia_url: result?.constancia_url || null,
        fecha_cumplimiento: constanciaFecha,
        notas_cumplimiento: constanciaNotas,
      });
      setShowConstanciaForm(false);
      setConstanciaFile(null);
      setConstanciaFecha("");
      setConstanciaNotas("");
    } catch (_) {}
    finally { setSavingConstancia(false); }
  };

  const handleSave = async () => {
    if (!forma || !fecha) return;
    setSaving(true);
    const formaLbl = nombreForma(forma);
    const isConvenio = formaLbl === "Convenio";
    const isSentencia = formaLbl === "Sentencia / Laudo favorable" || formaLbl === "Sentencia / Laudo desfavorable";
    try {
      await dispatch(actionSaveTerminacion(expedienteId, {
        id_forma_terminacion: parseInt(forma),
        fecha_terminacion: fecha,
        etapa: etapa || "",
        observaciones: obs || "",
        active: 1,
        monto_total: isConvenio ? (montoConvenio || null) : isSentencia ? (montoCondenado || null) : null,
        tipo_pago: isConvenio && tipoPago ? parseInt(tipoPago) : null,
        fecha_pago: isConvenio && tipoPago === "1" ? (fechaPagoConvenio || null) : null,
        parcialidades: isConvenio && tipoPago === "2"
          ? parcialidades.map((p) => ({ monto: p.monto, fecha_pago: p.fecha_pago || null }))
          : [],
      }));

      for (const pf of pendingFiles) {
        await dispatch(actionUploadTerminacionDoc(expedienteId, pf.file));
      }
      setPendingFiles([]);

      await dispatch(actionGetTerminacion(expedienteId));
      onDatosActualizados?.();
      setShowConfirm(false);
      setView("viewer");
    } catch (_) {
    } finally {
      setSaving(false);
    }
  };

  const handleReabrir = async () => {
    setSaving(true);
    try {
      await dispatch(actionSaveTerminacion(expedienteId, {
        id_forma_terminacion: parseInt(forma) || 1,
        fecha_terminacion: fecha,
        etapa: etapa || "",
        observaciones: obs || "",
        active: 0,
      }));
      setForma("");
      setFecha("");
      setEtapa("");
      setObs("");
      setPendingFiles([]);
      setMontoConvenio("");
      setMontoCondenado("");
      setTipoPago("");
      setFechaPagoConvenio("");
      setParcialidades([]);
      setShowReabrir(false);
      setMotivoReabrir("");
      setView("form");
      onDatosActualizados?.();
    } catch (_) {
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarTerminacion = async () => {
    setDeleting(true);
    try {
      await dispatch(actionDeleteTerminacion(expedienteId));
      await dispatch(actionGetTerminacion(expedienteId));
      setForma("");
      setFecha("");
      setEtapa("");
      setObs("");
      setPendingFiles([]);
      setMontoConvenio("");
      setMontoCondenado("");
      setTipoPago("");
      setFechaPagoConvenio("");
      setParcialidades([]);
      setConstanciaItem(null);
      setShowConstanciaForm(true);
      setConstanciaFile(null);
      setConstanciaFecha("");
      setConstanciaNotas("");
      setShowEliminar(false);
      setView("form");
      onDatosActualizados?.();
    } catch (_) {
    } finally {
      setDeleting(false);
    }
  };

  const openDoc = (doc) => {
    const url = doc.url?.startsWith("/api") ? doc.url : `/api${doc.url}`;
    window.open(`${PATH}${doc.url}`, "_blank", "noopener,noreferrer");
  };

  if (loading && !item && view === "form") {
    return <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Cargando...</div>;
  }

  // ── VIEWER ──────────────────────────────────────────────────────────────────
  if (view === "viewer") {
    return (
      <div style={{ color: C.text, padding: "24px 0" }}>
        {/* Card */}
        <div className="laboral-exp-detail-card laboral-exp-detail-card-main" style={{
          background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, padding: 20, marginBottom: 20,
        }}>
          {/* Header */}
          <div className="laboral-exp-card-header" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="laboral-exp-icon-badge" style={{ width: 44, height: 44, fontSize: 20, background: C.greenLight, color: C.green }}>
                <i className="fa-solid fa-circle-check" />
              </div>
              <div>
                <h2 className="laboral-exp-card-header-title" style={{ fontSize: 16 }}>Terminación del Juicio</h2>
                <span style={{ fontSize: 13, color: C.muted }}>Este expediente ha sido cerrado</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{
                padding: "4px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                background: C.greenLight, color: C.green, border: `1px solid ${C.greenBorder}`,
              }}>
                Terminado — {nombreForma(forma)}
              </span>
              <button
                onClick={() => setView("form")}
                className="laboral-exp-btn-clean"
                style={{ padding: "8px 18px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}
              >
                Editar
              </button>
              <button
                onClick={() => setShowEliminar(true)}
                style={{
                  padding: "8px 18px", borderRadius: 8, border: "none",
                  background: C.red, color: "#fff", fontSize: 13, cursor: "pointer", fontWeight: 700,
                  display: "inline-flex", alignItems: "center", gap: 6, transition: "background-color .15s ease",
                }}
                onMouseEnter={e => e.currentTarget.style.background = C.redHover}
                onMouseLeave={e => e.currentTarget.style.background = C.red}
              >
                Eliminar terminación
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginTop: 20, marginBottom: 28 }}>
            <InfoCard label="Forma de terminación" value={nombreForma(forma)} />
            <InfoCard label="Fecha de terminación" value={fecha} />
            <InfoCard label="Etapa de cierre" value={etapa || "—"} />
          </div>

          {/* Sección condicional viewer */}
          {(() => {
            const lbl = nombreForma(forma);
            const isConvenio = lbl === "Convenio";
            const isSentencia = lbl === "Sentencia / Laudo favorable" || lbl === "Sentencia / Laudo desfavorable";

            if (isConvenio && (montoConvenio || tipoPago)) return (
              <div style={{ background: C.greenLight, border: `1px solid ${C.greenBorder}`, borderRadius: 10, padding: 20, marginBottom: 28 }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, color: C.green }}><i className="fa-solid fa-handshake" style={{ marginRight: 8 }} /> Datos del Convenio</div>
                <div style={{ display: "grid", gridTemplateColumns: tipoPago === "1" ? "1fr 1fr 1fr" : "1fr 1fr", gap: 16, marginBottom: tipoPago === "2" && parcialidades.length > 0 ? 20 : 0 }}>
                  <div style={{ padding: 14, borderRadius: 10, background: C.white, border: `1px solid ${C.greenBorder}` }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Monto total convenido</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>${formatCurrency(montoConvenio) || "0.00"}</div>
                  </div>
                  <div style={{ padding: 14, borderRadius: 10, background: C.white, border: `1px solid ${C.greenBorder}` }}>
                    <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Tipo de pago</div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{tipoPago === "1" ? "Pago único" : tipoPago === "2" ? "Parcialidades" : "—"}</div>
                  </div>
                  {tipoPago === "1" && (
                    <div style={{ padding: 14, borderRadius: 10, background: C.white, border: `1px solid ${C.greenBorder}` }}>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Fecha de pago</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{fechaPagoConvenio ? fechaPagoConvenio.replace("T", " ") : "—"}</div>
                    </div>
                  )}
                </div>
                {tipoPago === "2" && parcialidades.length > 0 && (
                  <div>
                    <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 1fr 90px", gap: 8, padding: "4px 8px", borderBottom: `1px solid ${C.greenBorder}`, marginBottom: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>#</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Monto</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Fecha y hora de pago</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Estado</span>
                    </div>
                    {parcialidades.map((p, i) => (
                      <div key={p.localId || i} style={{ display: "grid", gridTemplateColumns: "40px 1fr 1fr 90px", gap: 8, alignItems: "center", marginBottom: 8 }}>
                        <div style={{ width: 26, height: 26, borderRadius: "50%", background: C.green, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{i + 1}</div>
                        <span style={{ fontSize: 14, fontWeight: 500 }}>${formatCurrency(p.monto) || "0.00"}</span>
                        <span style={{ fontSize: 14, color: C.muted }}>
                          {p.fecha_pago ? p.fecha_pago.replace("T", " ") : "—"}
                        </span>
                        <span style={{ padding: "4px 10px", borderRadius: 20, background: "#FEF9C3", color: "#854D0E", fontSize: 12, fontWeight: 600, textAlign: "center" }}>Pendiente</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );

            if (isSentencia && montoCondenado) return (
              <div style={{ background: C.blueLight, border: `1px solid ${C.blueBorder}`, borderRadius: 10, padding: 20, marginBottom: 28 }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, color: C.blue }}><i className="fa-solid fa-scale-balanced" style={{ marginRight: 8 }} /> Monto de la Sentencia</div>
                <div style={{ padding: 14, borderRadius: 10, background: C.white, border: `1px solid ${C.blueBorder}`, display: "inline-block", minWidth: 220 }}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Monto condenado</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>${formatCurrency(montoCondenado)}</div>
                </div>
              </div>
            );

            return null;
          })()}

          {/* Constancia de cumplimiento — solo si es Convenio */}
          {nombreForma(forma) === "Convenio" && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: C.text }}>Documento de la audiencia</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

                {/* Columna izquierda: documentos de soporte del convenio */}
                <div className="laboral-exp-detail-card" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", height: "100%" }}>
                  <div className="laboral-exp-card-header">
                    <div className="laboral-exp-icon-badge" style={{ width: 44, height: 44, fontSize: 20 }}>
                      <i className="fa-solid fa-file-lines" />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>Documento del convenio</div>
                      {documentos.length > 0
                        ? <div style={{ fontSize: 12, color: C.muted }}>{documentos[0].filename || documentos[0].path}</div>
                        : <div style={{ fontSize: 12, color: C.muted }}>Sin documento adjunto</div>
                      }
                    </div>
                  </div>
                  {documentos.length > 0 ? (
                    <>
                      <span style={{ display: "inline-block", padding: "3px 12px", borderRadius: 20, background: C.greenLight, color: C.green, fontSize: 12, fontWeight: 600, marginBottom: 14 }}>Finalizado</span>
                      <button
                        onClick={() => { const d = documentos[0]; window.open(`${PATH}${d.url}`, "_blank", "noopener,noreferrer"); }}
                        style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", padding: "9px 0", borderRadius: 10, background: C.blue, color: "#fff", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", marginTop: "auto" }}
                      >
                        Ver Documento ↗
                      </button>
                    </>
                  ) : (
                    <>
                      <div
                        onClick={() => convenioInputRef.current?.click()}
                        style={{
                          border: `2px dashed ${C.border}`, borderRadius: 10, padding: "18px 12px",
                          textAlign: "center", color: C.muted, fontSize: 13,
                          cursor: uploadingConvenio ? "not-allowed" : "pointer",
                          background: "#fff", marginBottom: 0,
                        }}
                      >
                        {uploadingConvenio
                          ? <><div style={{ fontSize: 22, marginBottom: 6 }}><i className="fa-solid fa-spinner fa-spin" /></div><div>Subiendo...</div></>
                          : <><div style={{ fontSize: 28, marginBottom: 6 }}><i className="fa-solid fa-upload" /></div><div>Subir un archivo o arrastrar y soltar</div><div style={{ fontSize: 11, marginTop: 4, color: "#9CA3AF" }}>PDF, PNG, JPG hasta 10MB</div></>
                        }
                      </div>
                      <input
                        ref={convenioInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: "none" }}
                        onChange={handleDirectUploadConvenio}
                      />
                    </>
                  )}
                </div>

                {/* Columna derecha: constancia de cumplimiento */}
                <div className="laboral-exp-detail-card" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, display: "flex", flexDirection: "column", height: "100%" }}>
                  <div className="laboral-exp-card-header">
                    <div className="laboral-exp-icon-badge" style={{ width: 44, height: 44, fontSize: 20 }}>
                      <i className="fa-solid fa-circle-check" />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>
                        {constanciaItem ? "Constancia de Cumplimiento" : "Cumplimiento de Convenio"}
                      </div>
                      {constanciaItem
                        ? <div style={{ fontSize: 12, color: C.muted }}>{constanciaItem.filename || "Constancia cargada"}</div>
                        : <div style={{ fontSize: 12, color: C.muted }}>Cargar constancia de cumplimiento</div>
                      }
                    </div>
                  </div>

                  {constanciaItem ? (
                    <>
                      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted, minWidth: 0 }}>
                            <i className="fa-regular fa-calendar" />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              Fecha de cumplimiento: <strong style={{ color: C.text, fontWeight: 600 }}>{constanciaItem.fecha_cumplimiento || "—"}</strong>
                            </span>
                          </div>
                          <span style={{ flex: "0 0 auto", padding: "3px 12px", borderRadius: 20, background: C.greenLight, color: C.green, fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <i className="fa-solid fa-check" /> Validado
                          </span>
                        </div>
                        {constanciaItem.notas_cumplimiento && (
                          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.border}` }}>
                            <div style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Notas</div>
                            <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.6 }}>{constanciaItem.notas_cumplimiento}</div>
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                        {constanciaItem.constancia_url && (
                          <button
                            onClick={() => window.open(`${PATH}${constanciaItem.constancia_url}`, "_blank", "noopener,noreferrer")}
                            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, flex: 1, minWidth: 0, padding: "9px 0", borderRadius: 10, background: C.blue, color: "#fff", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer" }}
                          >
                            Ver Constancia ↗
                          </button>
                        )}
                        <button
                          onClick={() => { setShowConstanciaForm(true); setConstanciaFecha(constanciaItem.fecha_cumplimiento || ""); setConstanciaNotas(constanciaItem.notas_cumplimiento || ""); }}
                          className="laboral-exp-btn-clean"
                          style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                            flex: constanciaItem.constancia_url ? "0 0 auto" : 1,
                            padding: "9px 16px", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
                          }}
                        >
                          <i className="fa-solid fa-pen" style={{ fontSize: 11 }} /> Actualizar
                        </button>
                      </div>
                    </>
                  ) : showConstanciaForm ? (
                    <div>
                      {/* Drop zone archivo */}
                      <div
                        onClick={() => constanciaInputRef.current?.click()}
                        style={{
                          border: `2px dashed ${constanciaFile ? C.green : C.border}`, borderRadius: 10, padding: "18px 12px",
                          textAlign: "center", color: C.muted, fontSize: 13, cursor: "pointer", marginBottom: 14, background: constanciaFile ? C.greenLight : "#fff",
                        }}
                      >
                        {constanciaFile
                          ? <><div style={{ fontSize: 18, marginBottom: 4 }}><i className="fa-solid fa-file-lines" style={{ color: C.green }} /></div><div style={{ fontWeight: 600, color: C.green }}>{constanciaFile.name}</div></>
                          : <><div style={{ fontSize: 28, marginBottom: 6 }}><i className="fa-solid fa-upload" /></div><div>Subir un archivo o arrastrar y soltar</div><div style={{ fontSize: 11, marginTop: 4, color: "#9CA3AF" }}>PDF, PNG, JPG hasta 10MB</div></>
                        }
                      </div>
                      <input ref={constanciaInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }}
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) setConstanciaFile(f); e.target.value = ""; }} />

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div>
                          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 4 }}>* Fecha de cumplimiento</label>
                          <ScrollDatePicker
                            value={constanciaFecha ? dayjs(constanciaFecha) : null}
                            onChange={(v) => setConstanciaFecha(v ? v.format("YYYY-MM-DD") : "")}
                            format={fmtFechaLarga}
                            placeholder="dd/mm/yyyy"
                            style={{ width: "100%" }}
                          />
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 4 }}>Notas de cumplimiento</label>
                          <textarea value={constanciaNotas} onChange={(e) => setConstanciaNotas(e.target.value)}
                            placeholder="Describe detalles adicionales..." style={{ ...input, minHeight: 60, resize: "vertical" }} />
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                        <button onClick={() => { setShowConstanciaForm(false); setConstanciaFile(null); setConstanciaFecha(""); setConstanciaNotas(""); }} style={btnSecondary}>
                          Cancelar
                        </button>
                        <button
                          onClick={handleSaveConstancia}
                          disabled={savingConstancia || !constanciaFecha}
                          style={{ ...btnPrimary, background: !constanciaFecha ? "#D1D5DB" : C.blue, cursor: !constanciaFecha ? "not-allowed" : "pointer" }}
                        >
                          {savingConstancia ? "Guardando..." : "Guardar"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowConstanciaForm(true)}
                      style={{ display: "block", width: "100%", padding: "11px 0", borderRadius: 8, background: C.blue, color: "#fff", fontSize: 14, fontWeight: 600, border: "none", cursor: "pointer" }}
                    >
                      + Cargar constancia
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Observaciones */}
          {obs && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div className="laboral-exp-icon-badge" style={{ width: 32, height: 32, fontSize: 14 }}>
                  <i className="fa-solid fa-note-sticky" />
                </div>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Observaciones</span>
              </div>
              <div style={{
                padding: 14, borderRadius: 8, background: C.bg, border: `1px solid ${C.border}`,
                fontSize: 13, lineHeight: 1.7, color: C.muted,
              }}>
                {obs}
              </div>
            </div>
          )}

          {/* Documentos */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div className="laboral-exp-icon-badge" style={{ width: 32, height: 32, fontSize: 14 }}>
                <i className="fa-solid fa-folder-open" />
              </div>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Documentos adjuntos</span>
            </div>
            {documentos.length === 0 && !constanciaItem?.constancia_url ? (
              <div style={{ color: C.muted, fontSize: 13 }}>Sin documentos adjuntos.</div>
            ) : (
              <>
                {documentos.map((d) => (
                  <div key={d.id_tribunal_terminacion_documentos} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
                    marginBottom: 8, background: C.bg,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                        <i className="fa-solid fa-file-lines" />
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{d.filename || d.path}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{d.path}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => openDoc(d)}
                      style={{
                        padding: "4px 14px", borderRadius: 6, border: "1px solid #e2e8f0",
                        background: "#f1f5f9", fontSize: 12, cursor: "pointer", color: "#475569", fontWeight: 700,
                      }}
                    >
                      Ver
                    </button>
                  </div>
                ))}
                {constanciaItem?.constancia_url && (
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
                    marginBottom: 8, background: C.bg,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div className="laboral-exp-icon-badge" style={{ width: 36, height: 36, fontSize: 16 }}>
                        <i className="fa-solid fa-circle-check" />
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{constanciaItem.filename || "Constancia de Cumplimiento"}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{constanciaItem.fecha_cumplimiento || ""}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => window.open(`${PATH}${constanciaItem.constancia_url}`, "_blank", "noopener,noreferrer")}
                      style={{
                        padding: "4px 14px", borderRadius: 6, border: "1px solid #e2e8f0",
                        background: "#f1f5f9", fontSize: 12, cursor: "pointer", color: "#475569", fontWeight: 700,
                      }}
                    >
                      Ver
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Modal reabrir */}
        {showReabrir && (
          <Overlay>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12, color: C.orange }}><i className="fa-solid fa-rotate" /></div>
              <h3 style={{ margin: "0 0 8px", fontSize: 18 }}>¿Reabrir expediente?</h3>
              <p style={{ color: C.muted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
                El expediente volverá al estado <strong>activo</strong>. Esta acción quedará registrada.
              </p>
              <Field label="Motivo de reapertura *">
                <textarea
                  value={motivoReabrir}
                  onChange={(e) => setMotivoReabrir(e.target.value)}
                  placeholder="Ej: Incumplimiento del convenio de pago..."
                  style={{ ...input, minHeight: 64 }}
                />
              </Field>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 20 }}>
                <button onClick={() => setShowReabrir(false)} style={btnSecondary}>Cancelar</button>
                <button
                  onClick={handleReabrir}
                  disabled={saving || !motivoReabrir.trim()}
                  style={{ ...btnPrimary, background: C.orange }}
                >
                  {saving ? "..." : "Sí, reabrir"}
                </button>
              </div>
            </div>
          </Overlay>
        )}

        {/* Modal eliminar terminación */}
        {showEliminar && (
          <Overlay>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12, color: C.red }}><i className="fa-solid fa-trash" /></div>
              <h3 style={{ margin: "0 0 8px", fontSize: 18 }}>¿Eliminar terminación?</h3>
              <p style={{ color: C.muted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
                Se eliminará la forma de terminación, montos, fechas de pago, parcialidades,
                documentos del convenio y la constancia de cumplimiento. El expediente quedará
                como si nunca se hubiera registrado una terminación. Esta acción no se puede deshacer.
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <button onClick={() => setShowEliminar(false)} style={btnSecondary}>Cancelar</button>
                <button
                  onClick={handleEliminarTerminacion}
                  disabled={deleting}
                  style={{ ...btnPrimary, background: C.red }}
                >
                  {deleting ? "Eliminando..." : "Sí, eliminar"}
                </button>
              </div>
            </div>
          </Overlay>
        )}
      </div>
    );
  }

  // ── FORMULARIO ───────────────────────────────────────────────────────────────
  return (
    <div style={{ color: C.text, padding: "24px 0" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="laboral-exp-icon-badge" style={{ width: 44, height: 44, fontSize: 20 }}>
            <i className="fa-solid fa-folder-open" />
          </div>
          <div>
            <h2 className="laboral-exp-card-header-title" style={{ fontSize: 16 }}>Terminación del Juicio</h2>
            <span style={{ fontSize: 13, color: C.muted }}>Registra la forma en que concluye este expediente</span>
          </div>
        </div>
        <span style={{
          padding: "4px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700,
          background: C.blueLight, color: C.blue, border: `1px solid ${C.blueBorder}`,
        }}>
          Juicio en curso
        </span>
      </div>

      {/* Formulario */}
      <div className="laboral-exp-detail-card laboral-exp-detail-card-main" style={{
        background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, padding: 20, marginBottom: 20,
      }}>
        {/* Fila principal */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 24 }}>
          <Field label="Forma de terminación *">
            <select
              value={forma}
              onChange={(e) => {
                setForma(e.target.value);
                setMontoConvenio("");
                setMontoCondenado("");
                setTipoPago("");
                setFechaPagoConvenio("");
                setParcialidades([]);
              }}
              style={select}
            >
              <option value="">Seleccionar...</option>
              {opcionesForma.map((op) => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Fecha de terminación *">
            <ScrollDatePicker
              value={fecha ? dayjs(fecha) : null}
              onChange={(v) => setFecha(v ? v.format("YYYY-MM-DD") : "")}
              format={fmtFechaLarga}
              placeholder="dd/mm/yyyy"
              style={{ width: "100%" }}
            />
          </Field>
          <Field label="Etapa en la que terminó">
            <select value={etapa} onChange={(e) => setEtapa(e.target.value)} style={select}>
              <option value="">Precargar automáticamente</option>
              {etapas.map((et) => (
                <option key={et} value={et}>{et}</option>
              ))}
            </select>
          </Field>
        </div>

        {/* Sección condicional según forma */}
        {(() => {
          const lbl = nombreForma(forma);
          const isConvenio = lbl === "Convenio";
          const isSentencia = lbl === "Sentencia / Laudo favorable" || lbl === "Sentencia / Laudo desfavorable";

          if (isConvenio) return (
            <div style={{ background: C.greenLight, border: `1px solid ${C.greenBorder}`, borderRadius: 10, padding: 20, marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, color: C.green }}><i className="fa-solid fa-handshake" style={{ marginRight: 8 }} /> Datos del Convenio</div>
              <div style={{ display: "grid", gridTemplateColumns: tipoPago === "1" ? "1fr 1fr 1fr" : "1fr 1fr", gap: 20, marginBottom: tipoPago === "2" ? 20 : 0 }}>
                <Field label="Monto total convenido *">
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: C.muted, pointerEvents: "none" }}>$</span>
                    <CurrencyInput
                      value={montoConvenio}
                      onChange={setMontoConvenio}
                      style={{ ...input, paddingLeft: 24 }}
                    />
                  </div>
                </Field>
                <Field label="Tipo de pago *">
                  <select
                    value={tipoPago}
                    onChange={(e) => { setTipoPago(e.target.value); setFechaPagoConvenio(""); setParcialidades([]); }}
                    style={{ ...select, borderColor: C.greenBorder }}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="1">Pago único</option>
                    <option value="2">Parcialidades</option>
                  </select>
                </Field>
                {tipoPago === "1" && (
                  <Field label="Fecha de pago *">
                    <ScrollDatePicker
                      showTime={{ format: "HH:mm" }}
                      needConfirm={false}
                      value={fechaPagoConvenio ? dayjs(fechaPagoConvenio) : null}
                      onChange={(v) => setFechaPagoConvenio(v ? v.format("YYYY-MM-DDTHH:mm") : "")}
                      format={fmtFechaLargaHora}
                      placeholder="dd/mm/yyyy hh:mm"
                      style={{ width: "100%" }}
                    />
                  </Field>
                )}
              </div>
              {tipoPago === "2" && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>Parcialidades</span>
                    <button
                      type="button"
                      onClick={(e) => { triggerHappyWave(e); addParcialidad(); }}
                      style={{ padding: "5px 16px", borderRadius: 8, border: "none", background: C.blue, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                    >
                      + Agregar parcialidad
                    </button>
                  </div>
                  {parcialidades.length > 0 && (
                    <div>
                      <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 1fr 90px 36px", gap: 8, padding: "4px 8px", borderBottom: `1px solid ${C.greenBorder}`, marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>#</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Monto</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Fecha y hora de pago</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Estado</span>
                        <span />
                      </div>
                      {parcialidades.map((p, i) => (
                        <div key={p.localId} style={{ display: "grid", gridTemplateColumns: "40px 1fr 1fr 90px 36px", gap: 8, alignItems: "center", marginBottom: 8 }}>
                          <div style={{ width: 26, height: 26, borderRadius: "50%", background: C.green, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>
                            {i + 1}
                          </div>
                          <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: C.muted, pointerEvents: "none" }}>$</span>
                            <CurrencyInput
                              value={p.monto}
                              onChange={(v) => updateParcialidad(p.localId, "monto", v)}
                              style={{ ...input, padding: "7px 10px", paddingLeft: 24 }}
                            />
                          </div>
                          <ScrollDatePicker
                            showTime={{ format: "HH:mm" }}
                            needConfirm={false}
                            value={p.fecha_pago ? dayjs(p.fecha_pago) : null}
                            onChange={(v) => updateParcialidad(p.localId, "fecha_pago", v ? v.format("YYYY-MM-DDTHH:mm") : "")}
                            format={fmtFechaLargaHora}
                            placeholder="dd/mm/yyyy hh:mm"
                            style={{ width: "100%" }}
                          />
                          <span style={{ padding: "4px 10px", borderRadius: 20, background: "#FEF9C3", color: "#854D0E", fontSize: 12, fontWeight: 600, textAlign: "center" }}>
                            Pendiente
                          </span>
                          <button
                            type="button"
                            onClick={() => removeParcialidad(p.localId)}
                            style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #FECACA", background: "#FEF2F2", color: C.red, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}
                          ><i className="fa-solid fa-xmark" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );

          if (isSentencia) return (
            <div style={{ background: C.blueLight, border: `1px solid ${C.blueBorder}`, borderRadius: 10, padding: 20, marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, color: C.blue }}><i className="fa-solid fa-scale-balanced" style={{ marginRight: 8 }} /> Monto de la Sentencia</div>
              <Field label="Monto condenado *">
                <CurrencyInput
                  value={montoCondenado}
                  onChange={setMontoCondenado}
                  style={{ ...input, maxWidth: 300 }}
                />
              </Field>
            </div>
          );

          return null;
        })()}

        {/* Documentos */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div className="laboral-exp-icon-badge" style={{ width: 32, height: 32, fontSize: 14 }}>
              <i className="fa-solid fa-folder-open" />
            </div>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Documentos de soporte</span>
          </div>

          {/* Drop zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${C.border}`, borderRadius: 10, padding: "22px 16px",
              textAlign: "center", color: C.muted, fontSize: 13, cursor: "pointer",
              transition: "border-color 0.2s", marginBottom: 12,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.blue)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}
          >
            <div style={{ fontSize: 26, marginBottom: 6 }}><i className="fa-solid fa-upload" /></div>
            Arrastra archivos aquí o{" "}
            <span style={{ color: C.blue, fontWeight: 600 }}>haz clic para seleccionar</span>
            <div style={{ fontSize: 11, marginTop: 4, color: "#9CA3AF" }}>PDF, DOCX, JPG — Máximo 10 MB por archivo</div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            style={{ display: "none" }}
            onChange={handleFilePick}
          />

          {/* Documentos ya guardados */}
          {documentos.map((d) => (
            <div key={d.id_tribunal_terminacion_documentos} style={docRow}>
              <i className="fa-solid fa-file-lines" style={{ fontSize: 16, color: C.muted }} />
              <span style={{ flex: 1, fontSize: 13 }}>{d.filename || d.path}</span>
              <button
                onClick={() => openDoc(d)}
                style={{ ...btnMini, color: C.blue, borderColor: C.blueBorder, background: C.blueLight }}
              >
                Ver
              </button>
              <button
                onClick={() => handleDeleteDoc(d.id_tribunal_terminacion_documentos)}
                style={{ ...btnMini, color: C.red, borderColor: "#FECACA", background: "#FEF2F2" }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          ))}

          {/* Archivos pendientes por subir */}
          {pendingFiles.map((pf) => (
            <div key={pf.localId} style={{ ...docRow, background: "#F0FDF4", borderColor: C.greenBorder }}>
              <i className="fa-solid fa-file-lines" style={{ fontSize: 16, color: C.muted }} />
              <span style={{ flex: 1, fontSize: 13 }}>{pf.name}</span>
              <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>Por subir</span>
              <button
                onClick={() => removePending(pf.localId)}
                style={{ ...btnMini, color: C.red, borderColor: "#FECACA", background: "#FEF2F2" }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
          ))}
        </div>

        {/* Observaciones */}
        <Field label="Observaciones">
          <textarea
            placeholder="Notas adicionales sobre la terminación del juicio..."
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            style={{ ...input, minHeight: 80, resize: "vertical" }}
          />
        </Field>
      </div>

      {/* Acciones */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
        {item && (
          <button onClick={() => setView("viewer")} style={btnSecondary}>
            Cancelar
          </button>
        )}
        <button
          onClick={() => setShowConfirm(true)}
          disabled={!forma || !fecha}
          style={{
            ...btnPrimary,
            background: !forma || !fecha ? "#D1D5DB" : C.blue,
            cursor: !forma || !fecha ? "not-allowed" : "pointer",
          }}
        >
          Guardar terminación
        </button>
      </div>

      {/* Modal confirmación */}
      {showConfirm && (
        <Overlay>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 12, color: C.orange }}><i className="fa-solid fa-triangle-exclamation" /></div>
            <h3 style={{ margin: "0 0 8px", fontSize: 18 }}>¿Confirmar terminación?</h3>
            <p style={{ color: C.muted, fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
              El expediente se marcará como <strong>{nombreForma(forma)}</strong> con fecha{" "}
              <strong>{fecha}</strong>.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={() => setShowConfirm(false)} style={btnSecondary}>Cancelar</button>
              <button onClick={handleSave} disabled={saving} style={btnPrimary}>
                {saving ? "Guardando..." : "Sí, guardar"}
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 1000,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: 32, maxWidth: 460,
        width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
      }}>
        {children}
      </div>
    </div>
  );
}

const btnPrimary = {
  padding: "10px 28px", borderRadius: 8, border: "none",
  background: C.blue, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
};

const btnSecondary = {
  padding: "10px 20px", borderRadius: 12, border: "1px solid #E2E8F0",
  background: "#F1F5F9", color: "#475569", fontSize: 14, cursor: "pointer", fontWeight: 700,
};

const btnMini = {
  padding: "3px 10px", borderRadius: 6, border: "1px solid",
  fontSize: 12, cursor: "pointer", fontWeight: 500,
};

const docRow = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "9px 12px", borderRadius: 8, border: "1px solid #E2E8F0",
  marginBottom: 8, background: "#F8FAFC",
};
